import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Link,
    Paper,
    Radio,
    RadioGroup,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { exportTrainingSet, listResearchLessons } from '../../services/researchService';
import { saveFile } from '../../services/download';

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : '—');
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

// The best label per utterance: final, else human, else model (call map section 15)
export const LABEL_COLORS = { final: '#1F6B31', human: '#1565C0', model: '#9C7BD0' };

const FILTERS = [
    { key: 'all', label: 'All', test: () => true },
    { key: 'uploads', label: 'Uploads', test: (l) => l.source === 'upload' },
    { key: 'inRun', label: 'In a run', test: (l) => Boolean(l.stageRun) },
    { key: 'final', label: 'All final', test: (l) => l.labels.final === l.utterances },
];

const STAGES = {
    final: { label: 'Final labels', color: '#1F6B31', background: '#E6F4EA' },
    in_adjudication: { label: 'In adjudication', color: '#8A5A00', background: '#FFF6E0' },
    in_annotation: { label: 'In annotation', color: '#1157A3', background: '#E8F1FB' },
    shared: { label: 'Shared, not in a run', color: '#4B5565', background: '#EDF0F2' },
};

const mixText = (labels) => ['final', 'human', 'model'].filter((k) => labels[k]).map((k) => `${fmt(labels[k])} ${k}`).join(' · ') || 'no labels';

const sumLabels = (rows) => rows.reduce((acc, l) => ({ final: acc.final + l.labels.final, human: acc.human + l.labels.human, model: acc.model + l.labels.model }), { final: 0, human: 0, model: 0 });

export const LabelBar = ({ labels, total }) => (
    <Box aria-hidden sx={{ display: 'flex', gap: '2px', height: 6, width: 140, borderRadius: 999, overflow: 'hidden', bgcolor: 'action.selected', mt: 0.5 }}>
        {['final', 'human', 'model'].map((k) => labels[k] > 0 && <Box key={k} sx={{ width: `${(labels[k] / total) * 100}%`, bgcolor: LABEL_COLORS[k] }} />)}
    </Box>
);

const basisText = (r) => (r.basis === 'study1_irb' ? 'Study 1 IRB terms' : r.basis === 'protocol' ? `Research protocol${r.at ? ` · ${shortDate(r.at)}` : ''}` : `Opted in · ${shortDate(r.at)}`);

// Frame 3: the only choice is which label each utterance gets; counts follow the choice
const ExportDialog = ({ rows, onClose, onDone }) => {
    const [rule, setRule] = useState('best');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const sum = sumLabels(rows);
    const counts = { best: sum.final + sum.human + sum.model, final: sum.final, final_or_human: sum.final + sum.human };
    const options = [
        { value: 'best', label: 'Best available (default)', detail: `final › human › model · ${fmt(counts.best)} utterances: ${mixText(sum)}` },
        { value: 'final', label: 'Final labels only', detail: `utterances without a final label are left out · ${fmt(counts.final)} utterances` },
        { value: 'final_or_human', label: 'Final or human labels only', detail: `model-only utterances are left out · ${fmt(counts.final_or_human)} utterances` },
    ];

    const download = async () => {
        setBusy(true);
        setError(null);
        try {
            const result = await exportTrainingSet({ lessonIds: rows.map((r) => r.lessonId), rule });
            const at = result.asOf ? new Date(result.asOf) : new Date();
            const name = result.fileName || `adas-training-export_${at.toISOString().slice(0, 16).replace('T', '_').replace(':', '')}.zip`;
            saveFile(result.blob, name);
            onDone({ name, at, lessons: rows.length, utterances: result.utterances || counts[rule], sum, rule, sha256: result.sha256 });
        } catch (err) {
            setBusy(false);
            setError(err.response?.status === 409 ? 'This rule leaves every utterance out. Choose another.' : 'Couldn’t build the export. Nothing was written. Try again.');
        }
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="export-title">
            <DialogTitle id="export-title" sx={{ typography: 'h4' }}>Export {rows.length} {rows.length === 1 ? 'lesson' : 'lessons'} for training</DialogTitle>
            <DialogContent>
                <Typography id="export-rule" variant="subtitle2" sx={{ mb: 1 }}>Which label does each utterance get?</Typography>
                <RadioGroup aria-labelledby="export-rule" value={rule} onChange={(e) => setRule(e.target.value)} sx={{ gap: 1 }}>
                    {options.map((o) => (
                        <FormControlLabel
                            key={o.value}
                            value={o.value}
                            control={<Radio />}
                            sx={{ alignItems: 'flex-start', m: 0, p: 1, border: '1px solid', borderColor: rule === o.value ? 'primary.main' : 'divider', borderRadius: 1.5 }}
                            label={<Box sx={{ pt: 0.75 }}><Typography variant="body2" sx={{ fontWeight: 600 }}>{o.label}</Typography><Typography variant="caption" color="text.secondary">{o.detail}</Typography></Box>}
                        />
                    ))}
                </RadioGroup>
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 2 }}>
                    Every utterance records where its label came from (label_source_ut, label_source_rc); RC is exported in 4 classes for the current classifier, with the 5 classes alongside. No train, validation or test split: split by lesson after download.
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
                    The file holds utterance text from K-12 classrooms: keep it in the approved storage, never email or Slack. The export is logged (who, when, lessons, rule).
                </Alert>
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={download} disabled={busy || counts[rule] === 0} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>Download export</Button>
            </DialogActions>
        </Dialog>
    );
};

// /lessons, the researcher's Lessons page (wf-research-lessons 1–4, YES-272): every consented upload
// and every imported lesson, its stage and label mix. Ticks are page state only: ADAS keeps no
// training-set state, only an audit event per export (decision 2026-10-25).
const LessonLibrary = () => {
    const { can } = useAuth();
    const canExport = can('trainingset.export');
    const [lessons, setLessons] = useState(null);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState('all');
    const [query, setQuery] = useState('');
    const [ticked, setTicked] = useState(new Set());
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(25);
    const [exporting, setExporting] = useState(false);
    const [exported, setExported] = useState(null);

    const load = useCallback(async () => {
        setFailed(false);
        try {
            setLessons(await listResearchLessons());
        } catch (err) {
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const all = useMemo(() => lessons || [], [lessons]);
    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        const test = FILTERS.find((f) => f.key === filter).test;
        return all.filter((l) => test(l) && (!q || l.code.toLowerCase().includes(q) || (l.name || '').toLowerCase().includes(q)));
    }, [all, filter, query]);
    useEffect(() => setPage(0), [filter, query]);

    const pageRows = shown.slice(page * perPage, page * perPage + perPage);
    const exportable = (l) => l.labels.final + l.labels.human + l.labels.model > 0;
    const selectable = pageRows.filter(exportable);
    const tickedRows = all.filter((l) => ticked.has(l.lessonId));
    const pageTicked = selectable.filter((l) => ticked.has(l.lessonId)).length;
    const sum = sumLabels(tickedRows);

    const toggle = (id) => setTicked((t) => {
        const next = new Set(t);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const tickPage = (on) => setTicked((t) => {
        const next = new Set(t);
        selectable.forEach((l) => (on ? next.add(l.lessonId) : next.delete(l.lessonId)));
        return next;
    });
    const allInFilter = shown.filter(exportable);
    const pageAllTicked = selectable.length > 0 && pageTicked === selectable.length;

    return (
        <Box sx={{ maxWidth: 1240, mx: 'auto' }}>
            <Typography variant="h2" component="h1" sx={{ mt: { xs: 1, sm: 2 } }}>Lessons</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: 820 }}>
                Consented teacher uploads and imported lessons. Open a lesson to compare its label layers.{canExport ? ' To build a training set, filter, tick whole lessons and export them.' : ''}
            </Typography>

            {exported && (
                <Alert severity="success" onClose={() => setExported(null)} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Downloaded {exported.name}</Typography>
                    <Typography variant="body2">
                        As of {exported.at.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })} · {exported.lessons} lessons · {fmt(exported.utterances)} utterances: {mixText(exported.sum)} · rule: {{ best: 'best available', final: 'final only', final_or_human: 'final or human' }[exported.rule]} · RC in 4 classes, 5 classes alongside · no split{exported.sha256 ? ` · sha256 ${exported.sha256.slice(0, 4)}…${exported.sha256.slice(-4)}` : ''} · keep it in the approved storage, never email or Slack.
                    </Typography>
                </Alert>
            )}
            {failed && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>Couldn’t load the lessons.</Alert>}

            {!failed && (
                <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)} aria-label="Filter lessons">
                            {FILTERS.map((f) => (
                                <ToggleButton key={f.key} value={f.key} sx={{ textTransform: 'none', px: 1.5 }}>
                                    {f.label} {lessons ? all.filter(f.test).length : ''}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <TextField
                            size="small"
                            placeholder="Search lesson ID or name"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            inputProps={{ 'aria-label': 'Search lesson ID or name' }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                            sx={{ flex: '1 1 240px', maxWidth: 360 }}
                        />
                    </Box>

                    {canExport && ticked.size > 0 && (
                        <Box role="status" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', px: 2, py: 1.25, mb: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {ticked.size} {ticked.size === 1 ? 'lesson' : 'lessons'} selected · {fmt(sum.final + sum.human + sum.model)} utterances: {mixText(sum)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" onClick={() => setTicked(new Set())}>Clear</Button>
                                <Button size="small" variant="contained" onClick={() => setExporting(true)}>Export selected…</Button>
                            </Box>
                        </Box>
                    )}
                    {canExport && pageAllTicked && allInFilter.length > selectable.length && ticked.size < allInFilter.length && (
                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                            All {selectable.length} lessons on this page are selected.{' '}
                            <Link component="button" type="button" onClick={() => setTicked((t) => new Set([...t, ...allInFilter.map((l) => l.lessonId)]))}>
                                Select all {allInFilter.length} lessons in this filter
                            </Link>
                        </Typography>
                    )}

                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small" aria-label="Lessons" sx={{ minWidth: 900 }}>
                            <TableHead>
                                <TableRow>
                                    {canExport && (
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={pageAllTicked}
                                                indeterminate={pageTicked > 0 && !pageAllTicked}
                                                onChange={(e) => tickPage(e.target.checked)}
                                                inputProps={{ 'aria-label': 'Select the lessons on this page' }}
                                            />
                                        </TableCell>
                                    )}
                                    {['Lesson', 'Source', 'Research use', 'Utterances', 'Labels (best per utterance)', 'Stage'].map((h) => <TableCell key={h}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!lessons && [0, 1, 2, 3].map((i) => <TableRow key={i}>{[0, 1, 2, 3, 4, 5, 6].map((j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
                                {pageRows.map((l) => {
                                    const stage = STAGES[l.stage];
                                    return (
                                        <TableRow key={l.lessonId} hover selected={ticked.has(l.lessonId)}>
                                            {canExport && (
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={ticked.has(l.lessonId)}
                                                        disabled={!exportable(l)}
                                                        onChange={() => toggle(l.lessonId)}
                                                        inputProps={{ 'aria-label': `Select ${l.code}` }}
                                                    />
                                                </TableCell>
                                            )}
                                            <TableCell>
                                                <Link component={RouterLink} to={`/research/lessons/${encodeURIComponent(l.lessonId)}`} underline="hover" sx={{ fontWeight: 700 }}>{l.code}</Link>
                                                {l.name && <Typography variant="caption" component="p" color="text.secondary">{l.name}</Typography>}
                                            </TableCell>
                                            <TableCell>
                                                {l.source === 'import' ? 'Study 1 import' : 'Upload'}
                                                {l.bundle && <Typography variant="caption" component="p" color="text.secondary">bundle {l.bundle}</Typography>}
                                            </TableCell>
                                            <TableCell>{basisText(l.researchUse)}</TableCell>
                                            <TableCell>{fmt(l.utterances)}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{mixText(l.labels)}</Typography>
                                                <LabelBar labels={l.labels} total={l.utterances} />
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={stage.label} sx={{ borderRadius: 999, fontWeight: 600, color: stage.color, bgcolor: stage.background }} />
                                                {l.stageRun && (
                                                    <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 0.25 }}>
                                                        {l.stage === 'final' ? 'run ' : ''}{l.stageRun.name}{typeof l.stageRun.done === 'number' ? ` · ${l.stageRun.done} of ${l.stageRun.total} items` : ''}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {lessons && shown.length === 0 && (
                                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>No lessons match.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                        <Box component="ul" aria-label="Legend" sx={{ display: 'flex', gap: 1.5, m: 0, p: 0, listStyle: 'none' }}>
                            {['final', 'human', 'model'].map((k) => (
                                <Box component="li" key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: LABEL_COLORS[k] }} />
                                    <Typography variant="caption">{k}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <TablePagination
                            component="div"
                            count={shown.length}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            rowsPerPage={perPage}
                            onRowsPerPageChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
                            rowsPerPageOptions={[25, 50, 100]}
                        />
                    </Box>
                    <Typography variant="caption" component="p" color="text.secondary">
                        Best label per utterance: final › human (annotation, or original for imports) › model. Analysis-only uploads never appear. Showing {shown.length} of {all.length}.
                    </Typography>
                </Paper>
            )}

            {exporting && (
                <ExportDialog
                    rows={tickedRows}
                    onClose={() => setExporting(false)}
                    onDone={(info) => { setExporting(false); setExported(info); }}
                />
            )}
        </Box>
    );
};

export default LessonLibrary;
